<template>
  <div class="project-intro">
    <!-- 顶部导航栏 -->
    <header class="intro-header">
      <div class="header-container">
        <div class="header-left">
          <router-link to="/" class="back-btn">
            <i class="el-icon-arrow-left"></i>
            <span>返回首页</span>
          </router-link>
        </div>
        <div class="header-title">{{ projectData.title }}</div>
        <div class="header-right">
          <el-button v-if="isLoggedIn" type="text" @click="goDashboard">
            <i class="el-icon-user"></i> 个人中心
          </el-button>
          <el-button v-else type="primary" size="small" @click="goLogin">登录</el-button>
        </div>
      </div>
    </header>

    <!-- 主要内容区域 -->
    <main class="intro-main">
      <!-- Hero 区域 -->
      <section class="hero-section" :style="{ backgroundImage: 'url(' + projectData.heroImage + ')' }">
        <div class="hero-overlay"></div>
        <div class="hero-content">
          <div class="hero-badge" v-if="projectData.badge">{{ projectData.badge }}</div>
          <h1 class="hero-title">{{ projectData.title }}</h1>
          <p class="hero-subtitle">{{ projectData.subtitle }}</p>
        </div>
      </section>

      <!-- 项目简介 -->
      <section class="section intro-section">
        <div class="section-container">
          <h2 class="section-title">
            <i class="el-icon-info"></i>
            项目简介
          </h2>
          <div class="intro-content">
            <p v-for="(para, idx) in projectData.introduction" :key="idx">{{ para }}</p>
          </div>
        </div>
      </section>

      <!-- AMC深度专题（amc8/amc10/amc12都显示） -->
      <section v-if="isAmcProject" class="article-section">
        <!-- 文章头部横幅 -->
        <div class="article-hero">
          <div class="article-hero-content">
            <div class="article-tag">深度专题</div>
            <h1 class="article-title">AMC数学竞赛在中国</h1>
            <p class="article-subtitle">从"爬藤利器"到"全民数学素养测试"的十年进化</p>
            <div class="article-meta">
              <span class="article-date">2026年度深度解读</span>
              <span class="article-readtime">阅读约8分钟</span>
            </div>
          </div>
        </div>

        <!-- 章节导航卡片 -->
        <div class="article-toc">
          <div class="toc-container">
            <div class="toc-card" v-for="(chapter, idx) in articleChapters" :key="idx" @click="scrollToChapter(idx)">
              <div class="toc-number">0{{ idx + 1 }}</div>
              <div class="toc-title">{{ chapter.title }}</div>
            </div>
          </div>
        </div>

        <!-- 导语 -->
        <div class="article-lead">
          <div class="lead-container">
            <div class="lead-quote-mark">"</div>
            <p class="lead-text">当上海一位四年级小学生周末坐在电脑前，用40分钟完成25道中英双语的数学题；当南京家长在朋友圈晒出孩子的AMC8全球前5%证书——这已是今天中国城市家庭教育中越来越常见的场景。</p>
            <p class="lead-highlight">曾经专属于"爬藤族"的美国数学竞赛（AMC），正在蜕变为中国中产家庭检验孩子数学思维能力的"全民标尺"。</p>
          </div>
        </div>

        <!-- 第一章 -->
        <article class="article-chapter" ref="chapter0">
          <div class="chapter-header">
            <div class="chapter-badge">第一章</div>
            <h2 class="chapter-title">七十年沉淀</h2>
            <p class="chapter-subtitle">从美国本土测试到全球数学人才的"筛子"</p>
          </div>
          <div class="chapter-content">
            <div class="content-text">
              <p>AMC的历史比很多人想象的更久远。<strong>1950年</strong>，美国数学协会（MAA）创办了这项赛事，初衷很简单：为美国中学生提供一个公平、有挑战性的数学竞技平台。那时它还叫"美国高中数学考试"（AHSME），远没有今天的国际影响力。</p>
              <p>真正的转折发生在<strong>1985年</strong>和<strong>2000年</strong>。1985年，MAA增设初中组赛事（AJHSME），首次将触角伸向低龄学生；2000年，这套体系被整合为今天我们所熟知的<strong>AMC8/10/12三级金字塔结构</strong>。</p>
              <div class="info-highlight">
                <i class="el-icon-s-promotion"></i>
                <span>从此，AMC不再只是一场考试，而是一套完整的人才筛选流水线：AMC8启蒙 → AMC10/12选拔 → AIME进阶 → USAMO乃至IMO国家队</span>
              </div>
              <p>七十年后的今天，AMC的年参赛规模已突破<strong class="highlight-number">60万</strong>人次，覆盖全球<strong class="highlight-number">20</strong>余个国家。MIT、布朗、卡内基梅隆等大学在申请系统中专门设有AMC/AIME成绩填报栏——这几乎是标化考试之外极少数被顶尖理工院校"强制要求"填写的竞赛成绩。</p>
            </div>
            <figure class="chapter-figure">
              <img :src="amcLongRead.images[0].src" :alt="amcLongRead.images[0].alt" />
              <figcaption>{{ amcLongRead.images[0].caption }}</figcaption>
            </figure>
          </div>
        </article>

        <!-- 第二章 -->
        <article class="article-chapter chapter-alt" ref="chapter1">
          <div class="chapter-header">
            <div class="chapter-badge">第二章</div>
            <h2 class="chapter-title">升学地图重绘</h2>
            <p class="chapter-subtitle">出国与国内两条迥异的起跑线</p>
          </div>
          <div class="chapter-content content-reverse">
            <figure class="chapter-figure">
              <img :src="amcLongRead.images[1].src" :alt="amcLongRead.images[1].alt" />
              <figcaption>{{ amcLongRead.images[1].caption }}</figcaption>
            </figure>
            <div class="content-text">
              <h3 class="section-title-alt">
                <i class="el-icon-s-flag"></i>
                出国赛道：从"敲门砖"到"新标化"
              </h3>
              <p>对于目标海外升学的家庭，AMC的价值曲线已从"增益"走向"必须"。当大量中国申请者都能交出SAT数学满分、AP微积分BC满分时，这类标化成绩已无法区分谁是真正的数学思考者。</p>
              <div class="data-flow">
                <div class="flow-item">
                  <div class="flow-dot"></div>
                  <div class="flow-text">
                    <strong>Top 2.5%-5%</strong>
                    <span>晋级AIME，Top50-30敲门资格</span>
                  </div>
                </div>
                <div class="flow-item">
                  <div class="flow-dot"></div>
                  <div class="flow-text">
                    <strong>AIME 7分+</strong>
                    <span>Top30录取概率显著提升</span>
                  </div>
                </div>
                <div class="flow-item">
                  <div class="flow-dot gold"></div>
                  <div class="flow-text">
                    <strong>前1%</strong>
                    <span>Top20硬通货</span>
                  </div>
                </div>
              </div>
              <h3 class="section-title-alt">
                <i class="el-icon-map-location"></i>
                国内升学：冷热不均的地图
              </h3>
              <p>在上海，AMC8早已是"小升初择校的隐形加分项"——部分顶尖初中在评估生源时会明确关注这一成绩。2025-2026年教育部新规后，国内竞赛升学红利收窄，但AMC反而更回归本质。</p>
            </div>
          </div>
        </article>

        <!-- 第三章 -->
        <article class="article-chapter" ref="chapter2">
          <div class="chapter-header">
            <div class="chapter-badge">第三章</div>
            <h2 class="chapter-title">竞赛真空与逆向填补</h2>
            <p class="chapter-subtitle">小学生的AMC8启蒙</p>
          </div>
          <div class="chapter-content">
            <div class="content-text">
              <div class="stat-card-row">
                <div class="stat-card">
                  <div class="stat-number-large">26%</div>
                  <div class="stat-label">小学4-6年级参赛占比</div>
                </div>
                <div class="stat-card">
                  <div class="stat-number-large">3-4年</div>
                  <div class="stat-label">起跑线提前时间</div>
                </div>
              </div>
              <p>国内几乎没有面向小学阶段的官方、高含金量数学竞赛。官方的留白，恰恰成了AMC8的进场空间。AMC8在中国赛区的参赛主力军，早已不是其名义对象"8年级及以下"，而是<strong>小学4-6年级学生</strong>。</p>
              <div class="quote-box">
                <p>"这不是'提前学'，而是'提前适应'——适应竞赛语言（中英双语）、适应题型节奏（40分钟25题）、适应那种'每一步都需要独立推理'的思维方式。"</p>
              </div>
            </div>
            <figure class="chapter-figure">
              <img :src="amcLongRead.images[2].src" :alt="amcLongRead.images[2].alt" />
              <figcaption>{{ amcLongRead.images[2].caption }}</figcaption>
            </figure>
          </div>
        </article>

        <!-- 第四章 - 数据可视化 -->
        <article class="article-chapter chapter-data" ref="chapter3">
          <div class="chapter-header center">
            <div class="chapter-badge">第四章</div>
            <h2 class="chapter-title">十四万人时代</h2>
            <p class="chapter-subtitle">数据背后的爆发与分化</p>
          </div>
          <div class="data-showcase">
            <div class="data-main">
              <div class="data-growth">
                <div class="growth-bar">
                  <div class="bar-item">
                    <span class="bar-year">2020</span>
                    <div class="bar-line" style="--height: 28%"></div>
                    <span class="bar-value">4万</span>
                  </div>
                  <div class="bar-item">
                    <span class="bar-year">2025</span>
                    <div class="bar-line highlight" style="--height: 100%"></div>
                    <span class="bar-value highlight">14万+</span>
                  </div>
                </div>
                <p class="growth-caption">五年规模翻两番，年均增长25%-30%</p>
              </div>
            </div>
            <div class="data-features">
              <div class="feature-item">
                <i class="el-icon-s-data"></i>
                <h4>低龄化</h4>
                <p>小学4-6年级占比显著提升</p>
              </div>
              <div class="feature-item">
                <i class="el-icon-trophy"></i>
                <h4>门槛上移</h4>
                <p>前1%需23题，前5%需19题</p>
              </div>
              <div class="feature-item">
                <i class="el-icon-s-claim"></i>
                <h4>难度螺旋</h4>
                <p>几何权重40%，数论组合融合</p>
              </div>
            </div>
          </div>
          <figure class="chapter-figure wide">
            <img :src="amcLongRead.images[3].src" :alt="amcLongRead.images[3].alt" />
            <figcaption>{{ amcLongRead.images[3].caption }}</figcaption>
          </figure>
        </article>

        <!-- 第五章 - 指南卡片 -->
        <article class="article-chapter chapter-guide" ref="chapter4">
          <div class="chapter-header">
            <div class="chapter-badge">第五章</div>
            <h2 class="chapter-title">2026赛季指南</h2>
            <p class="chapter-subtitle">赛程、报名与参赛方式</p>
          </div>
          <div class="guide-grid">
            <div class="guide-card">
              <div class="guide-icon">
                <i class="el-icon-date"></i>
              </div>
              <h3>AMC8</h3>
              <p class="guide-date">2026年1月23日 10:00-10:40</p>
              <p class="guide-note">8年级及以下，≤14.5周岁</p>
            </div>
            <div class="guide-card">
              <div class="guide-icon">
                <i class="el-icon-s-order"></i>
              </div>
              <h3>AMC10/12</h3>
              <p class="guide-date">A卷：2026年11月上旬</p>
              <p class="guide-date">B卷：2025年11月中旬</p>
              <p class="guide-note">AIME晋级线：10前2.5% / 12前5%</p>
            </div>
            <div class="guide-card highlight">
              <div class="guide-icon">
                <i class="el-icon-location"></i>
              </div>
              <h3>报名方式</h3>
              <p class="guide-highlight">校内考点通道</p>
              <p class="guide-or">或</p>
              <p class="guide-highlight">MAA授权机构代报名</p>
              <p class="guide-note">中英双语试卷，线下考试</p>
            </div>
          </div>
          <figure class="chapter-figure">
            <img :src="amcLongRead.images[4].src" :alt="amcLongRead.images[4].alt" />
            <figcaption>{{ amcLongRead.images[4].caption }}</figcaption>
          </figure>
        </article>

        <!-- 结语 -->
        <div class="article-epilogue">
          <div class="epilogue-container">
            <div class="epilogue-header">
              <i class="el-icon-reading"></i>
              <span>结语</span>
            </div>
            <h2 class="epilogue-title">当竞赛不再是捷径</h2>
            <p class="epilogue-text">那位四年级小学生花40分钟完成25道题，可能拿不到任何升学加分，甚至学校老师都不知道这张证书意味着什么。但他经历过这样一种测试：没有超纲的知识点，却需要每一步都独立思考；没有死记硬背的套路，却能在解题后感到"原来还可以这样想"。</p>
            <div class="epilogue-highlight">
              <p>当竞赛与升学的刚性挂钩逐渐被剥离，AMC因其"国际认证+思维导向+低龄友好"的属性，从留学工具进化为数学素养的度量衡。</p>
            </div>
            <p class="epilogue-closing">它不是捷径，而是一条需要提前三年开始慢跑的长赛道。AMC8那25道题的意义，早已超越了获奖与否——<strong>它是一扇门，门后是另一种看待数学的方式。</strong></p>
          </div>
        </div>
      </section>

      <!-- 袋鼠数学深度专题（仅kangaroo显示） -->
      <section v-if="projectType === 'kangaroo'" class="article-section">
        <!-- 文章头部横幅 -->
        <div class="article-hero" style="background: linear-gradient(135deg, #52c41a 0%, #1890ff 100%);">
          <div class="article-hero-content">
            <div class="article-tag">全球盛会</div>
            <h1 class="article-title">袋鼠数学：全球800万孩子"玩"进名校的秘密</h1>
            <p class="article-subtitle">它不是竞赛，是全世界最大的数学"兴趣班"</p>
            <div class="article-meta">
              <span class="article-date">2026年度深度解读</span>
              <span class="article-readtime">阅读约10分钟</span>
            </div>
          </div>
        </div>

        <!-- 章节导航卡片 -->
        <div class="article-toc">
          <div class="toc-container" style="grid-template-columns: repeat(6, 1fr);">
            <div class="toc-card" v-for="(chapter, idx) in kangarooChapters" :key="idx" @click="scrollToChapter(idx)">
              <div class="toc-number" style="color: #52c41a;">0{{ idx + 1 }}</div>
              <div class="toc-title">{{ chapter.title }}</div>
            </div>
          </div>
        </div>

        <!-- 导语 -->
        <div class="article-lead">
          <div class="lead-container">
            <div class="lead-quote-mark" style="color: #52c41a;">"</div>
            <p class="lead-text">如果你是一位小学低年级家长，正在为"该不该让孩子接触数学竞赛"而犹豫，那请你先看这样一组数字：<strong>2025年，全球106个国家和地区、超过780万名学生，在同一天参加了同一场数学考试。</strong></p>
            <p class="lead-highlight" style="border-left-color: #52c41a; background: linear-gradient(135deg, rgba(82, 196, 26, 0.05) 0%, rgba(24, 144, 255, 0.05) 100%);">780万是什么概念？它相当于中国每年高考总人数的2/3。而在这场考试中，仅中国赛区就有35万人报名，来自4200多所中小学。它的名字叫袋鼠数学竞赛（Math Kangaroo）。</p>
          </div>
        </div>

        <!-- 第一章 -->
        <article class="article-chapter" ref="chapter0">
          <div class="chapter-header">
            <div class="chapter-badge" style="background: linear-gradient(135deg, #52c41a 0%, #1890ff 100%);">第一章</div>
            <h2 class="chapter-title">我们如何误解了"含金量"</h2>
            <p class="chapter-subtitle">袋鼠数学不是选拔赛，它是普及型竞赛</p>
          </div>
          <div class="chapter-content">
            <div class="content-text">
              <p>在中国家长的认知里，一个竞赛的"含金量"几乎等价于"获奖难度"和"升学直通率"。这没有错，但它只适用于选拔型竞赛——比如AMC，比如高联。<strong>但袋鼠数学不是选拔赛，它是普及型竞赛。</strong></p>
              <p>它的创始人、澳大利亚数学家Peter O'Halloran在1980年构思这项赛事时，目标极其朴素：<strong>让更多孩子觉得数学不讨厌。</strong>1991年，法国两位数学教育家接过火炬，正式命名为"袋鼠数学思维趣味活动"，总部设于巴黎。</p>
              <div class="comparison-table">
                <div class="table-header">
                  <span>比较维度</span>
                  <span>其他数学竞赛</span>
                  <span>袋鼠数学</span>
                </div>
                <div class="table-row">
                  <span>核心目标</span>
                  <span>选拔数学天才</span>
                  <span class="highlight-cell">普及数学兴趣</span>
                </div>
                <div class="table-row">
                  <span>题目风格</span>
                  <span>抽象化、严肃化</span>
                  <span class="highlight-cell">生活化、游戏化</span>
                </div>
                <div class="table-row">
                  <span>获奖比例</span>
                  <span>全球前5%，1%</span>
                  <span class="highlight-cell">中国区前35%</span>
                </div>
                <div class="table-row">
                  <span>参与对象</span>
                  <span>5年级以上</span>
                  <span class="highlight-cell">1-4年级为主（70%）</span>
                </div>
              </div>
              <p>用足球类比：其他数学竞赛是职业俱乐部的青训选拔赛，而袋鼠数学是每个班级的体育课——你不需要成为梅西才能上场，但你得上场，才有可能发现自己喜欢踢球。</p>
            </div>
            <figure class="chapter-figure">
              <img :src="kangarooLongRead.images[0].src" :alt="kangarooLongRead.images[0].alt" />
              <figcaption>{{ kangarooLongRead.images[0].caption }}</figcaption>
            </figure>
          </div>
        </article>

        <!-- 第二章 -->
        <article class="article-chapter chapter-alt" ref="chapter1">
          <div class="chapter-header">
            <div class="chapter-badge" style="background: linear-gradient(135deg, #52c41a 0%, #1890ff 100%);">第二章</div>
            <h2 class="chapter-title">一个你可能从未听过的"袋鼠曲线"</h2>
            <p class="chapter-subtitle">从袋鼠金奖到AMC8全球前20%</p>
          </div>
          <div class="chapter-content content-reverse">
            <figure class="chapter-figure">
              <img :src="kangarooLongRead.images[1].src" :alt="kangarooLongRead.images[1].alt" />
              <figcaption>{{ kangarooLongRead.images[1].caption }}</figcaption>
            </figure>
            <div class="content-text">
              <p>在研究袋鼠数学长达八年的参赛数据后，我注意到一条隐形的成长曲线，姑且称它为<strong>"袋鼠曲线"</strong>：</p>
              <div class="kangaroo-path">
                <div class="path-step">
                  <div class="step-num">1</div>
                  <div class="step-text">三年级之前接触袋鼠并获铜奖以上</div>
                </div>
                <div class="path-arrow">→</div>
                <div class="path-step">
                  <div class="step-num">2</div>
                  <div class="step-text">五年级袋鼠冲刺超级金奖</div>
                </div>
                <div class="path-arrow">→</div>
                <div class="path-step highlight">
                  <div class="step-num">3</div>
                  <div class="step-text">六年级衔接AMC8并进入全球前20%</div>
                </div>
              </div>
              <div class="stat-grid">
                <div class="stat-box">
                  <div class="stat-value">60%</div>
                  <div class="stat-desc">袋鼠与AMC8知识点重叠率</div>
                </div>
                <div class="stat-box">
                  <div class="stat-value">30%</div>
                  <div class="stat-desc">袋鼠金奖得主AMC8获奖率提升</div>
                </div>
                <div class="stat-box">
                  <div class="stat-value">85%</div>
                  <div class="stat-desc">袋鼠金奖选手AMC8进前20%</div>
                </div>
              </div>
              <div class="quote-box" style="border-left-color: #52c41a;">
                <p>"我们不需要孩子小学就会解AMC8的难题，但我们需要他看到难题时不发抖。"——上海"三公"学校数学老师</p>
              </div>
            </div>
          </div>
        </article>

        <!-- 第三章 -->
        <article class="article-chapter" ref="chapter2">
          <div class="chapter-header">
            <div class="chapter-badge" style="background: linear-gradient(135deg, #52c41a 0%, #1890ff 100%);">第三章</div>
            <h2 class="chapter-title">那些"偷偷"把袋鼠写进简历的人</h2>
            <p class="chapter-subtitle">小升初简历里的"隐形加分项"</p>
          </div>
          <div class="chapter-content">
            <div class="content-text">
              <p>尽管教育部明文规定竞赛成绩不得与升学直接挂钩，但在真实的招生场景中，<strong>袋鼠证书正在成为小升初简历里的"隐形加分项"。</strong>这不是秘密，只是没人明说。</p>
              <h3 class="section-title-alt" style="color: #52c41a;">
                <i class="el-icon-school"></i>
                上海"三公"学校的筛选逻辑
              </h3>
              <p>上外附中、上海实验、浦外这三所上海顶尖公办学校的招生办，在筛选上万份简历时，面对两个数学成绩差不多的孩子——</p>
              <div class="resume-compare">
                <div class="resume-card">
                  <div class="resume-label">学生A</div>
                  <p>"校内数学全优"</p>
                </div>
                <div class="resume-vs">VS</div>
                <div class="resume-card highlight">
                  <div class="resume-label">学生B</div>
                  <p>"校内数学全优<br/><strong>+ 袋鼠数学金奖</strong>"</p>
                </div>
              </div>
              <p>招生官的真实心态是：袋鼠金奖至少说明这个孩子在小学中低年级就接触过逻辑训练、图形推理、应用题建模，<strong>他的数学天花板可能更高。</strong></p>
              <h3 class="section-title-alt" style="color: #52c41a;">
                <i class="el-icon-office-building"></i>
                国际学校的"优先录取"条款
              </h3>
              <p>上海星河湾双语学校、协和双语学校等顶尖民办国际学校，在低龄段（1-6年级）入学评估中，明确将袋鼠成绩列为学术能力参考。</p>
            </div>
            <figure class="chapter-figure">
              <img :src="kangarooLongRead.images[2].src" :alt="kangarooLongRead.images[2].alt" />
              <figcaption>{{ kangarooLongRead.images[2].caption }}</figcaption>
            </figure>
          </div>
        </article>

        <!-- 第四章 -->
        <article class="article-chapter chapter-alt" ref="chapter3">
          <div class="chapter-header">
            <div class="chapter-badge" style="background: linear-gradient(135deg, #52c41a 0%, #1890ff 100%);">第四章</div>
            <h2 class="chapter-title">780万人背后的"反常识"真相</h2>
            <p class="chapter-subtitle">获奖率高≠拿高奖容易</p>
          </div>
          <div class="chapter-content content-reverse">
            <figure class="chapter-figure">
              <img :src="kangarooLongRead.images[3].src" :alt="kangarooLongRead.images[3].alt" />
              <figcaption>{{ kangarooLongRead.images[3].caption }}</figcaption>
            </figure>
            <div class="content-text">
              <p>如果你第一次听说袋鼠数学，可能会有一个本能质疑：<strong>获奖率这么高，岂不是"水赛"？</strong></p>
              <p>这个质疑可以理解，但它忽略了一个关键事实：<strong>获奖率高≠拿高奖容易。</strong></p>
              <div class="score-table">
                <div class="score-header">
                  <span>级别</span>
                  <span>满分</span>
                  <span>超级金奖线</span>
                  <span>容错</span>
                </div>
                <div class="score-row">
                  <span>Level A（1-2年级）</span>
                  <span>120分</span>
                  <span class="highlight-score">114分</span>
                  <span>仅2题</span>
                </div>
                <div class="score-row">
                  <span>Level D（7-8年级）</span>
                  <span>150分</span>
                  <span class="highlight-score">145分</span>
                  <span>仅1题</span>
                </div>
              </div>
              <div class="info-highlight" style="background: linear-gradient(135deg, rgba(82, 196, 26, 0.08) 0%, rgba(24, 144, 255, 0.08) 100%);">
                <i class="el-icon-trophy" style="color: #52c41a;"></i>
                <span><strong>全国超级金奖获奖率不足1%</strong>，竞争强度与AMC8全球前5%相当。低门槛入场，高难度封神——这才是780万人赛事的真实面貌。</span>
              </div>
            </div>
          </div>
        </article>

        <!-- 第五章 -->
        <article class="article-chapter" ref="chapter4">
          <div class="chapter-header">
            <div class="chapter-badge" style="background: linear-gradient(135deg, #52c41a 0%, #1890ff 100%);">第五章</div>
            <h2 class="chapter-title">75分钟里的策略课</h2>
            <p class="chapter-subtitle">风险决策训练的绝佳场景</p>
          </div>
          <div class="chapter-content">
            <div class="content-text">
              <p>袋鼠数学还有一个被严重低估的教育价值：<strong>它是中国孩子极少数能接触到的"风险决策训练"。</strong></p>
              <div class="rule-box">
                <h4><i class="el-icon-collection-tag"></i> 评分规则</h4>
                <div class="rule-list">
                  <div class="rule-item correct">
                    <i class="el-icon-check"></i>
                    <span>答对得分</span>
                  </div>
                  <div class="rule-item wrong">
                    <i class="el-icon-close"></i>
                    <span>答错扣1分</span>
                  </div>
                  <div class="rule-item skip">
                    <i class="el-icon-minus"></i>
                    <span>不答不扣分</span>
                  </div>
                </div>
              </div>
              <p>这个规则意味着：<strong>不是所有题都要做，不是所有猜都值得赌。</strong></p>
              <div class="strategy-list">
                <div class="strategy-item">
                  <div class="strategy-num">1</div>
                  <div class="strategy-text">
                    <strong>快速识别"确定能对的题"</strong>
                    <span>这是基本盘，确保基础分</span>
                  </div>
                </div>
                <div class="strategy-item">
                  <div class="strategy-num">2</div>
                  <div class="strategy-text">
                    <strong>果断跳过"超时2分钟仍无思路的题"</strong>
                    <span>及时止损，避免扣分</span>
                  </div>
                </div>
                <div class="strategy-item">
                  <div class="strategy-num">3</div>
                  <div class="strategy-text">
                    <strong>精准判断"哪些模棱两可的题值得猜"</strong>
                    <span>策略性博弈，最大化得分</span>
                  </div>
                </div>
              </div>
              <p>在真实世界的竞争中，<strong>"知道自己不该做什么"往往比"知道该做什么"更重要。</strong></p>
            </div>
          </div>
        </article>

        <!-- 第六章 - 指南卡片 -->
        <article class="article-chapter chapter-guide" ref="chapter5">
          <div class="chapter-header">
            <div class="chapter-badge" style="background: linear-gradient(135deg, #52c41a 0%, #1890ff 100%);">第六章</div>
            <h2 class="chapter-title">2026年，你的孩子怎么参加？</h2>
            <p class="chapter-subtitle">赛程、报名与参赛方式</p>
          </div>
          <div class="guide-grid">
            <div class="guide-card">
              <div class="guide-icon" style="background: rgba(82, 196, 26, 0.1);">
                <i class="el-icon-date" style="color: #52c41a;"></i>
              </div>
              <h3>考试时间</h3>
              <p class="guide-date" style="color: #52c41a;">2026年3月19日（星期四）</p>
              <p class="guide-note">A-B级：上午10:00-11:15</p>
              <p class="guide-note">C-F级：下午14:00-15:15</p>
            </div>
            <div class="guide-card">
              <div class="guide-icon" style="background: rgba(24, 144, 255, 0.1);">
                <i class="el-icon-edit" style="color: #1890ff;"></i>
              </div>
              <h3>报名截止</h3>
              <p class="guide-date" style="color: #1890ff;">常规：2025年12月31日</p>
              <p class="guide-note">延迟：2026年2月1日</p>
              <p class="guide-note">（可能收取附加费）</p>
            </div>
            <div class="guide-card highlight" style="background: linear-gradient(135deg, #52c41a 0%, #1890ff 100%);">
              <div class="guide-icon" style="background: rgba(255, 255, 255, 0.2);">
                <i class="el-icon-location" style="color: #fff;"></i>
              </div>
              <h3>报名方式</h3>
              <p class="guide-highlight">学校集体报名</p>
              <p class="guide-or">或</p>
              <p class="guide-highlight">机构代报名</p>
              <p class="guide-note" style="color: rgba(255,255,255,0.9);">1-2年级提供中文语音读题</p>
            </div>
          </div>
          <div class="award-section">
            <h3><i class="el-icon-medal"></i> 奖项设置（中国区）</h3>
            <div class="award-grid">
              <div class="award-item super-gold">
                <div class="award-name">超级金奖</div>
                <div class="award-rate">全国前3%</div>
              </div>
              <div class="award-item gold">
                <div class="award-name">金奖</div>
                <div class="award-rate">全国前10%</div>
              </div>
              <div class="award-item silver">
                <div class="award-name">银奖</div>
                <div class="award-rate">全国前20%</div>
              </div>
              <div class="award-item bronze">
                <div class="award-name">铜奖</div>
                <div class="award-rate">全国前35%</div>
              </div>
              <div class="award-item skill">
                <div class="award-name">数学技能奖</div>
                <div class="award-rate">各赛区前25%</div>
              </div>
            </div>
          </div>
        </article>

        <!-- 结语 -->
        <div class="article-epilogue" style="background: linear-gradient(135deg, #135200 0%, #003a8c 100%);">
          <div class="epilogue-container">
            <div class="epilogue-header">
              <i class="el-icon-reading"></i>
              <span>结语</span>
            </div>
            <h2 class="epilogue-title" style="background: linear-gradient(135deg, #52c41a 0%, #69c0ff 100%); -webkit-background-clip: text;">数学不是筛子，是梯子</h2>
            <p class="epilogue-text">这篇文章写到这里，我想起杭州世外一位带队老师的话。2025年，他的学生拿了袋鼠满分成就奖、超级金奖，又从北京捧回了加州伯克利大学数学挑战活动的奖杯。有人在庆功宴上问："袋鼠这么'简单'，跟伯克利比赛能比吗？"</p>
            <div class="epilogue-highlight" style="border-left-color: #52c41a; background: rgba(82, 196, 26, 0.15);">
              <p>这位老师说了一段值得所有家长听的话："不是每个孩子都要当数学家，但每个孩子都值得在数学里赢一次。"</p>
            </div>
            <p class="epilogue-text">AMC是给数学王国的原住民准备的——他们生来就看得懂数字的密码。而袋鼠数学是给所有孩子准备的——那些还在门口张望、不确定自己要不要进去的孩子。</p>
            <p class="epilogue-closing">它不说"你不行"，它只问：<strong style="color: #69c0ff;">"小袋鼠，你要不要试试分糖果、走迷宫、找一找哪扇门后藏着奶酪？"</strong>780万人选择了"试"。如果你也在犹豫该不该让孩子接触数学竞赛，这就是你的答案：<strong style="color: #52c41a;">先上场，再谈输赢。</strong></p>
          </div>
        </div>
      </section>

      <!-- GESP深度专题（仅gesp显示） -->
      <section v-if="projectType === 'gesp'" class="article-section">
        <!-- 文章头部横幅 -->
        <div class="article-hero" style="background: linear-gradient(135deg, #722ed1 0%, #1890ff 100%);">
          <div class="article-hero-content">
            <div class="article-tag">权威认证</div>
            <h1 class="article-title">GESP：中国计算机学会为编程少年铺好的那条"隐形台阶"</h1>
            <p class="article-subtitle">从第一行代码到免试进复赛，CCF亲手铺的台阶</p>
            <div class="article-meta">
              <span class="article-date">2026年度深度解读</span>
              <span class="article-readtime">阅读约8分钟</span>
            </div>
          </div>
        </div>

        <!-- 章节导航卡片 -->
        <div class="article-toc">
          <div class="toc-container" style="grid-template-columns: repeat(5, 1fr);">
            <div class="toc-card" v-for="(chapter, idx) in gespChapters" :key="idx" @click="scrollToChapter(idx)">
              <div class="toc-number" style="color: #722ed1;">0{{ idx + 1 }}</div>
              <div class="toc-title">{{ chapter.title }}</div>
            </div>
          </div>
        </div>

        <!-- 导语 -->
        <div class="article-lead">
          <div class="lead-container">
            <div class="lead-quote-mark" style="color: #722ed1;">"</div>
            <p class="lead-text">如果你是2018年之前的家长，想让孩子学编程，路径是这样的：先去网上搜"少儿编程"，跳出一堆机构广告。报名、上课、学 Scratch、学 Python、学 C++。学了一阵，老师说"可以考级了"。你问：考什么级？老师说：有好几种，电子学会的、工信部的、某某协会的。你问：哪个有用？老师沉默了一下，说：都有用，但都不太有用。</p>
            <p class="lead-highlight" style="border-left-color: #722ed1; background: linear-gradient(135deg, rgba(114, 46, 209, 0.05) 0%, rgba(24, 144, 255, 0.05) 100%);">然后孩子到了四年级，老师说：可以试试信奥赛了。你问：怎么试？老师说：直接报 CSP-J。你问：那以前考的那些级有用吗？老师说：没用，从头来。<strong>你花了三年铺的路，到高速入口发现——根本不通。</strong>这就是 2022 年之前中国编程教育的真实困境。2023 年，中国计算机学会（CCF）下场了。</p>
          </div>
        </div>

        <!-- 第一章 -->
        <article class="article-chapter" ref="chapter0">
          <div class="chapter-header">
            <div class="chapter-badge" style="background: linear-gradient(135deg, #722ed1 0%, #1890ff 100%);">第一章</div>
            <h2 class="chapter-title">它不是什么"又一个考级"</h2>
            <p class="chapter-subtitle">GESP 是信奥赛官方铺设的第一段台阶</p>
          </div>
          <div class="chapter-content">
            <div class="content-text">
              <p>GESP 的全称是"CCF 编程能力等级认证"。如果你只读到"等级认证"四个字就把它归类为市面上那二三十种编程考级，你就错过了它最核心的价值。</p>
              <div class="info-highlight" style="background: linear-gradient(135deg, rgba(114, 46, 209, 0.08) 0%, rgba(24, 144, 255, 0.08) 100%);">
                <i class="el-icon-s-flag" style="color: #722ed1;"></i>
                <span><strong>GESP 不是考级，是信奥赛官方铺设的第一段台阶。</strong>这不是机构画的规划路线图，这是 CCF 用红头文件盖章的官方衔接通道。</span>
              </div>
              <p>你要先看懂这个流程：</p>
              <div class="gesp-path-flow">
                <div class="gesp-step">图形化1-4级</div>
                <div class="gesp-arrow">→</div>
                <div class="gesp-step">Python/C++1-8级</div>
                <div class="gesp-arrow">→</div>
                <div class="gesp-step highlight">GESP 7/8级高分</div>
                <div class="gesp-arrow">→</div>
                <div class="gesp-step">免CSP-J/S初赛</div>
                <div class="gesp-arrow">→</div>
                <div class="gesp-step">进复赛→NOIP→NOI</div>
              </div>
              <p>在此之前，信奥赛是一条"断头路"：小学生不能正式参赛（NOI 规定 12 岁以下不允许报名），初中生可以直接考 CSP-J，但小学阶段学的那些 Scratch、Python 考级证书，到了 CSP 考场门口全作废。</p>
              <div class="quote-box" style="border-left-color: #722ed1;">
                <p>你以前考的级，到了信奥赛门口是"请重新购票"。GESP 的 8 级证书，是"VIP 免排队通道"。<strong>这不是含金量的差异，这是体系内认证和体系外认证的差异。</strong></p>
              </div>
            </div>
            <figure class="chapter-figure">
              <img :src="gespLongRead.images[0].src" :alt="gespLongRead.images[0].alt" />
              <figcaption>{{ gespLongRead.images[0].caption }}</figcaption>
            </figure>
          </div>
        </article>

        <!-- 第二章 -->
        <article class="article-chapter chapter-alt" ref="chapter1">
          <div class="chapter-header">
            <div class="chapter-badge" style="background: linear-gradient(135deg, #722ed1 0%, #1890ff 100%);">第二章</div>
            <h2 class="chapter-title">为什么小学阶段非它不可？</h2>
            <p class="chapter-subtitle">填补小学编程竞赛的"真空期"</p>
          </div>
          <div class="chapter-content content-reverse">
            <figure class="chapter-figure">
              <img :src="gespLongRead.images[1].src" :alt="gespLongRead.images[1].alt" />
              <figcaption>{{ gespLongRead.images[1].caption }}</figcaption>
            </figure>
            <div class="content-text">
              <p>2025 年 CCF 再次明确：<strong>NOI 系列赛事不允许 12 周岁以下选手报名</strong>。这意味着什么？</p>
              <p>意味着一个 9 岁、10 岁的孩子，无论编程能力多强，都无法正式参加 CSP-J/S。他可以去机构模拟考，可以刷题库，但不能拥有官方成绩。而国内绝大多数小升初科技特长生的材料审核，只看官方成绩。</p>
              <div class="info-highlight" style="background: linear-gradient(135deg, rgba(114, 46, 209, 0.08) 0%, rgba(24, 144, 255, 0.08) 100%);">
                <i class="el-icon-s-check" style="color: #722ed1;"></i>
                <span>这就是小学阶段编程竞赛的"真空期"——比数学还严重。数学好歹还有袋鼠、AMC8 可以"曲线救国"，编程连曲线都没有。</span>
              </div>
              <p><strong>GESP 是目前唯一一个填补这个真空的官方通道：</strong></p>
              <ul class="benefit-list">
                <li><i class="el-icon-check" style="color: #722ed1;"></i> 它是 CCF 主办的，和 CSP、NOIP 同一个爸爸</li>
                <li><i class="el-icon-check" style="color: #722ed1;"></i> 它允许 6-18 岁全年龄段报名，没有 12 岁门槛</li>
                <li><i class="el-icon-check" style="color: #722ed1;"></i> 它的成绩在小升初简历里，是极少数能被招办老师认出来的编程证书</li>
              </ul>
              <div class="quote-box" style="border-left-color: #722ed1;">
                <p>一位上海家长在论坛里写过："孩子五年级，C++ 水平已经够 CSP-J 复赛了，但年龄不够报不了。我们就考 GESP，六级拿了 92 分，简历投进三公，面单来了。"<strong>你不能参加高考，但你可以先拿一模的成绩证明自己。</strong></p>
              </div>
            </div>
          </div>
        </article>

        <!-- 第三章 -->
        <article class="article-chapter" ref="chapter2">
          <div class="chapter-header">
            <div class="chapter-badge" style="background: linear-gradient(135deg, #722ed1 0%, #1890ff 100%);">第三章</div>
            <h2 class="chapter-title">不是"考过"，是"跳着考"</h2>
            <p class="chapter-subtitle">让能力强的孩子不被等级制度困住</p>
          </div>
          <div class="chapter-content">
            <div class="content-text">
              <p>GESP 另一个让老牌考级相形见绌的设计是：<strong>它允许跳级，而且跳级规则极其清晰</strong>。</p>
              <div class="jump-rules">
                <div class="rule-card">
                  <div class="rule-icon" style="background: #722ed1;">90+</div>
                  <div class="rule-text">
                    <h4>单科成绩 ≥90 分</h4>
                    <p>下一次可跳一级报考<br/>（1级考90分，下次直接报3级）</p>
                  </div>
                </div>
                <div class="rule-card">
                  <div class="rule-icon" style="background: #1890ff;">推荐</div>
                  <div class="rule-text">
                    <h4>CCF认证教师推荐</h4>
                    <p>首次报考可一次性跨级报名<br/>能力到了直接上</p>
                  </div>
                </div>
                <div class="rule-card">
                  <div class="rule-icon" style="background: #52c41a;">转考</div>
                  <div class="rule-text">
                    <h4>跨语言转考</h4>
                    <p>Python 1级考90分<br/>转 C++ 时可直接报 3 级</p>
                  </div>
                </div>
              </div>
              <p>这不是为了"省报名费"。它的真实价值是：<strong>让能力强的孩子不被等级制度困住</strong>。</p>
              <div class="info-highlight" style="background: linear-gradient(135deg, rgba(114, 46, 209, 0.08) 0%, rgba(24, 144, 255, 0.08) 100%);">
                <i class="el-icon-s-promotion" style="color: #722ed1;"></i>
                <span>市面上的编程考级，绝大多数是"逐级必考"——你 C++ 水平已经到四级了，对不起，请从一级开始交钱、报名、考试、等证书。这个过程短则一年，长则两年。<strong>GESP 的逻辑是：你的能力到了，就直接往上走。</strong></span>
              </div>
              <p>2025 年南京一个六年级学生，C++ 零基础，跟着机构集训了 8 个月，直接报考 GESP 四级，84 分通过。次月参加 CSP-J，初赛全省前 15%。如果他必须从一级开始考，到六年级毕业都未必能摸到七级的免试门槛。</p>
            </div>
            <figure class="chapter-figure">
              <img :src="gespLongRead.images[2].src" :alt="gespLongRead.images[2].alt" />
              <figcaption>{{ gespLongRead.images[2].caption }}</figcaption>
            </figure>
          </div>
        </article>

        <!-- 第四章 -->
        <article class="article-chapter chapter-alt" ref="chapter3">
          <div class="chapter-header">
            <div class="chapter-badge" style="background: linear-gradient(135deg, #722ed1 0%, #1890ff 100%);">第四章</div>
            <h2 class="chapter-title">2026年，有两件事变了</h2>
            <p class="chapter-subtitle">新规与考试时间</p>
          </div>
          <div class="chapter-content content-reverse">
            <figure class="chapter-figure">
              <img :src="gespLongRead.images[3].src" :alt="gespLongRead.images[3].alt" />
              <figcaption>{{ gespLongRead.images[3].caption }}</figcaption>
            </figure>
            <div class="content-text">
              <h3 class="section-title-alt" style="color: #722ed1;">
                <i class="el-icon-warning"></i>
                第一，选择题和判断题全面禁止使用编译软件
              </h3>
              <p>这条 2025 年底刚出的规定，外行家长可能看不懂——什么叫"禁用编译软件"？</p>
              <p>以前很多孩子考 GESP 笔试部分（选择题/判断题），习惯打开 IDE，把代码敲进去跑一下，看输出对不对，再回来选答案。这叫"编译验证法"。<strong>2026 年起，这是违规行为。一旦被系统检测到编译软件运行记录，成绩作废。</strong></p>
              <div class="quote-box" style="border-left-color: #f5a623;">
                <p>这意味着什么？GESP 正在刻意区分"会写代码"和"懂编程"。能跑出正确答案，不等于理解这段代码的逻辑。这是一次明确的"选拔前移"：<strong>如果你连笔试都要靠编译器帮忙，复赛的高强度算法构造你更扛不住。</strong></p>
              </div>
              <h3 class="section-title-alt" style="color: #722ed1;">
                <i class="el-icon-date"></i>
                第二，2026 年 GESP 第 13 次认证定于 3 月 14 日
              </h3>
              <div class="exam-info-cards">
                <div class="exam-card">
                  <div class="exam-label">考试时间</div>
                  <div class="exam-value">2026年3月14日</div>
                  <div class="exam-detail">1-4级：09:30-11:30<br/>5-8级：13:30-16:30</div>
                </div>
                <div class="exam-card">
                  <div class="exam-label">报名时间</div>
                  <div class="exam-value">1月19日-3月5日</div>
                  <div class="exam-detail">全年4次：3月、6月、9月、12月</div>
                </div>
                <div class="exam-card">
                  <div class="exam-label">收费标准</div>
                  <div class="exam-value">300-440元</div>
                  <div class="exam-detail">1级300元，逐级递增20元<br/>8级440元</div>
                </div>
              </div>
            </div>
          </div>
        </article>

        <!-- 第五章 - 指南卡片 -->
        <article class="article-chapter chapter-guide" ref="chapter4">
          <div class="chapter-header">
            <div class="chapter-badge" style="background: linear-gradient(135deg, #722ed1 0%, #1890ff 100%);">第五章</div>
            <h2 class="chapter-title">GESP不是终点，是"收费最低的入口"</h2>
            <p class="chapter-subtitle">通往复赛、省赛、全国赛的第一段台阶</p>
          </div>
          <div class="chapter-content">
            <div class="content-text">
              <p>最后说一个很多家长没算清的账。</p>
              <div class="cost-comparison">
                <div class="cost-item gesp">
                  <div class="cost-name">GESP</div>
                  <div class="cost-total">2960元</div>
                  <div class="cost-desc">1级300元 → 8级440元<br/>全部考完总花费</div>
                </div>
                <div class="cost-vs">VS</div>
                <div class="cost-list">
                  <div class="cost-item other">
                    <div class="cost-name">某电子学会</div>
                    <div class="cost-total">4000+元</div>
                  </div>
                  <div class="cost-item other">
                    <div class="cost-name">某工信部考级</div>
                    <div class="cost-total">3500+元</div>
                  </div>
                  <div class="cost-item other">
                    <div class="cost-name">某行业协会</div>
                    <div class="cost-total">5000+元</div>
                  </div>
                </div>
              </div>
              <div class="info-highlight" style="background: linear-gradient(135deg, rgba(82, 196, 26, 0.08) 0%, rgba(24, 144, 255, 0.08) 100%);">
                <i class="el-icon-s-claim" style="color: #52c41a;"></i>
                <span><strong>GESP 是头部编程考级里收费最低的，没有之一。</strong>但它的价值恰好不在"省钱"。它的价值是：当你的孩子通过 GESP 七级、八级，拿着 80 分以上的成绩单向 CSP-J/S 报名系统里输入免试资格码时，他会发现——过去两年交的每一次报名费，都没有白花。</span>
              </div>
            </div>
            <figure class="chapter-figure">
              <img :src="gespLongRead.images[4].src" :alt="gespLongRead.images[4].alt" />
              <figcaption>{{ gespLongRead.images[4].caption }}</figcaption>
            </figure>
          </div>

          <!-- 报考手册 -->
          <div class="guide-manual">
            <h3><i class="el-icon-reading" style="color: #722ed1;"></i> 2026年GESP报考实用手册</h3>
            <div class="manual-grid">
              <div class="manual-card">
                <h4><i class="el-icon-date"></i> 考试时间与报名</h4>
                <p><strong>第13次认证：</strong>2026年3月14日</p>
                <p>• 1-4级：上午 09:30-11:30</p>
                <p>• 5-8级：下午 13:30-16:30</p>
                <p><strong>报名时间：</strong>2026年1月19日17:00 - 3月5日24:00</p>
                <p><strong>全年考试：</strong>3月、6月、9月、12月（共4次）</p>
              </div>
              <div class="manual-card">
                <h4><i class="el-icon-location"></i> 报名通道</h4>
                <p>• <strong>官网：</strong>https://gesp.ccf.org.cn</p>
                <p>• <strong>微信公众号：</strong>"CCF GESP"</p>
                <p>• 无需学校合作：个人直接报名，选择就近考点</p>
              </div>
              <div class="manual-card">
                <h4><i class="el-icon-trophy"></i> 跳级规则（关键策略）</h4>
                <p>• 单科 ≥90 分 → 下次可跳1级报考</p>
                <p>• 首次报考有 CCF 认证教师推荐 → 可跨级报名</p>
                <p>• 跨语言转考（Python→C++）→ Python 1级≥90分，C++可直接报3级</p>
              </div>
              <div class="manual-card">
                <h4><i class="el-icon-warning"></i> 考试当天必带/禁带</h4>
                <p><strong style="color: #52c41a;">✅ 必带：</strong>纸质准考证、有效身份证件、两支笔、两张空白A4演算纸</p>
                <p><strong style="color: #f5222d;">❌ 禁带：</strong>手机、U盘、电子设备</p>
                <p><strong style="color: #f5222d;">❌ 严禁：</strong>选择题/判断题时段打开任何编译软件</p>
              </div>
            </div>
            <div class="contact-info">
              <h4><i class="el-icon-phone"></i> 官方咨询</h4>
              <p>• 邮箱：gesp@ccf.org.cn</p>
              <p>• 电话：0512-67656856（工作日 8:30-17:30）</p>
            </div>
          </div>
          <figure class="chapter-figure wide">
            <img :src="gespLongRead.images[5].src" :alt="gespLongRead.images[5].alt" />
            <figcaption>{{ gespLongRead.images[5].caption }}</figcaption>
          </figure>
        </article>

        <!-- 结语 -->
        <div class="article-epilogue" style="background: linear-gradient(135deg, #1a0f2e 0%, #0f1a3c 100%);">
          <div class="epilogue-container">
            <div class="epilogue-header">
              <i class="el-icon-reading"></i>
              <span>结语</span>
            </div>
            <h2 class="epilogue-title" style="background: linear-gradient(135deg, #b37feb 0%, #69c0ff 100%); -webkit-background-clip: text; background-clip: text;">把当年自己摸黑走过的路，装上路灯</h2>
            <p class="epilogue-text">1998年，第一场NOI只有不到200人参加，电脑是386，机房没有空调。二十八年过去，那批少年成了工程师、科学家、命题人。他们回过头，把当年自己摸黑走过的路，装上路灯。</p>
            <div class="epilogue-highlight" style="border-left-color: #722ed1; background: rgba(114, 46, 209, 0.15);">
              <p>所以有了GESP。不是又一个考级，是CCF亲手铺的台阶——从第一行cout，到免试进复赛，他们太知道孩子会卡在哪里：年龄不够、逐级必考、证书互不承认。所以把跳级写进章程，把8级80分变成直通证，把报名费定在300块。<strong>这不是生意，是交代。</strong></p>
            </div>
            <p class="epilogue-closing">路已经亮了二十八年。你只需要带孩子，<strong style="color: #b37feb;">走上第一级</strong>。</p>
          </div>
        </div>
      </section>

      <!-- 项目特点 -->
      <section class="section features-section">
        <div class="section-container">
          <h2 class="section-title">
            <i class="el-icon-star-on"></i>
            项目特点
          </h2>
          <div class="features-grid">
            <div v-for="(feature, idx) in projectData.features" :key="idx" class="feature-card">
              <div class="feature-icon" :style="{ backgroundColor: feature.color || '#1890ff' }">
                <i :class="feature.icon || 'el-icon-check'"></i>
              </div>
              <h3 class="feature-title">{{ feature.title }}</h3>
              <p class="feature-desc">{{ feature.desc }}</p>
            </div>
          </div>
        </div>
      </section>

      <!-- 适合人群 -->
      <section class="section audience-section">
        <div class="section-container">
          <h2 class="section-title">
            <i class="el-icon-user"></i>
            适合人群
          </h2>
          <div class="audience-list">
            <div v-for="(item, idx) in projectData.audience" :key="idx" class="audience-item">
              <i class="el-icon-check"></i>
              <span>{{ item }}</span>
            </div>
          </div>
        </div>
      </section>

      <!-- 学习内容 -->
      <section class="section content-section">
        <div class="section-container">
          <h2 class="section-title">
            <i class="el-icon-reading"></i>
            学习内容
          </h2>
          <div class="content-list">
            <div v-for="(item, idx) in projectData.learningContent" :key="idx" class="content-item">
              <div class="content-number">{{ idx + 1 }}</div>
              <div class="content-info">
                <h4>{{ item.title }}</h4>
                <p>{{ item.desc }}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- 袋鼠数学特有的级别选择 -->
      <section v-if="projectType === 'kangaroo'" class="section level-section">
        <div class="section-container">
          <h2 class="section-title">
            <i class="el-icon-s-flag"></i>
            选择级别
          </h2>
          <p class="level-hint">请根据您的年级选择合适的级别：</p>
          <div class="level-grid">
            <div
              v-for="level in kangarooLevels"
              :key="level.value"
              class="level-card"
              :class="{ active: selectedLevel === level.value }"
              @click="selectedLevel = level.value"
            >
              <div class="level-badge">LEVEL {{ level.value }}</div>
              <div class="level-name">{{ level.name }}</div>
              <div class="level-grade">{{ level.grade }}</div>
            </div>
          </div>
        </div>
      </section>

      <!-- 底部行动区域 -->
      <section class="section action-section">
        <div class="section-container">
          <div class="action-box">
            <div class="action-info">
              <h3>准备好开始学习了吗？</h3>
              <p>{{ projectData.actionHint || '立即开始您的学习之旅，提升数学思维能力！' }}</p>
            </div>
            <el-button
              type="primary"
              size="large"
              class="action-btn"
              :loading="checkingPlan"
              :disabled="(projectType === 'kangaroo' && !selectedLevel) || checkingPlan"
              @click="enterStudy"
            >
              <i v-if="!checkingPlan" class="el-icon-right"></i>
              {{ checkingPlan ? '正在检查学习计划...' : '进入学习' }}
            </el-button>
          </div>
        </div>
      </section>
    </main>

    <!-- 页脚 -->
    <footer class="intro-footer">
      <div class="footer-container">
        <p>© {{ new Date().getFullYear() }} 悉迹AI · 国际学术挑战平台</p>
      </div>
    </footer>
  </div>
</template>

<script>
import { getToken } from '@/utils/auth'
import { getTokenInfo } from '@/utils/jwtUtils'

// 长文图片配置
const amcLongRead = {
  images: [
    { src: '/amc-longread-01.png', alt: '数学人柱力', caption: '数学人柱力' },
    { src: '/amc-longread-02.png', alt: '通往AMC的阶梯', caption: '通往AMC的阶梯' },
    { src: '/amc-longread-03.png', alt: 'AMC冷热对比', caption: 'AMC冷热对比' },
    { src: '/amc-longread-04.png', alt: 'AMC8起跑线', caption: 'AMC8起跑线' },
    { src: '/amc-longread-05.png', alt: '蜗牛爬藤', caption: '蜗牛爬藤' }
  ]
}

// 袋鼠数学长文配置
const kangarooLongRead = {
  images: [
    { src: '/amc-longread-12.png', alt: '袋鼠数学全球参与', caption: '全球780万孩子参与的数学盛会' },
    { src: '/amc-longread-13.png', alt: '袋鼠曲线', caption: '从袋鼠到AMC的成长曲线' },
    { src: '/amc-longread-14.png', alt: '简历加分', caption: '小升初简历的隐形加分项' },
    { src: '/amc-longread-15.png', alt: '获奖真相', caption: '780万人背后的真相' }
  ]
}

// GESP长文配置
const gespLongRead = {
  images: [
    { src: '/amc-longread-06.png', alt: '隐形台阶', caption: 'CCF为编程少年铺好的隐形台阶' },
    { src: '/amc-longread-07.png', alt: '信奥赛通道', caption: '通往信奥赛的官方衔接通道' },
    { src: '/amc-longread-08.png', alt: '小学阶段', caption: '填补小学编程竞赛真空期' },
    { src: '/amc-longread-09.png', alt: '跳级规则', caption: '能力到了就往上走' },
    { src: '/amc-longread-10.png', alt: '2026新规', caption: '2026年GESP两项重要变化' },
    { src: '/amc-longread-11.png', alt: '报考手册', caption: '2026年GESP报考实用手册' }
  ]
}

// 章节配置
const articleChapters = [
  { title: '七十年沉淀' },
  { title: '升学地图' },
  { title: '逆向填补' },
  { title: '十四万人' },
  { title: '赛季指南' }
]

// 袋鼠数学章节
const kangarooChapters = [
  { title: '含金量误解' },
  { title: '袋鼠曲线' },
  { title: '简历秘密' },
  { title: '反常识真相' },
  { title: '策略课' },
  { title: '2026指南' }
]

// GESP章节
const gespChapters = [
  { title: '不是又一个考级' },
  { title: '小学阶段非它不可' },
  { title: '跳着考' },
  { title: '2026年新规' },
  { title: '最低收费入口' }
]

// 项目数据配置
const projectConfigs = {
  amc8: {
    title: '美国数学测评 AMC',
    subtitle: '全球最具影响力的中学数学竞赛，培养数学思维与解题能力',
    badge: '热门',
    heroImage: 'https://images.unsplash.com/photo-1596495578065-6e0763fa1178?auto=format&fit=crop&w=1600&q=80',
    introduction: [
      'AMC（American Mathematics Competitions）是美国数学协会（MAA）主办的全球性数学竞赛，涵盖AMC 8、AMC 10、AMC 12三个级别，分别面向8年级及以下、10年级及以下、12年级及以下的学生。',
      '该测评旨在激发学生对数学的兴趣，培养解决问题的能力和数学思维。题目设计注重考查学生的逻辑推理能力、创新思维和数学应用能力。',
      'AMC是通往更高级别数学竞赛（AIME、USAMO、IMO）的重要通道，也是申请美国顶尖高中和大学的重要学术背景之一，被全球6000多所学校认可。'
    ],
    features: [
      { title: '国际认可', desc: '全球超过6000所学校参与，成绩被国际名校认可', icon: 'el-icon-trophy', color: '#f5a623' },
      { title: '思维训练', desc: '培养逻辑思维、创新能力和问题解决技巧', icon: 'el-icon-cpu', color: '#1890ff' },
      { title: '竞赛衔接', desc: '为 AIME、USAMO、IMO 等高级竞赛打下基础', icon: 'el-icon-connection', color: '#52c41a' },
      { title: '升学助力', desc: '丰富学术背景，助力名校申请', icon: 'el-icon-school', color: '#722ed1' }
    ],
    audience: [
      '对数学有兴趣的中学生',
      '希望提升数学思维能力的学生',
      '计划参加更高级别数学竞赛的学生',
      '有志于申请国际名校的学生'
    ],
    learningContent: [
      { title: '基础知识巩固', desc: '系统复习数学核心知识点，夯实基础' },
      { title: '题型专项训练', desc: '针对 AMC 常见题型进行专项突破' },
      { title: '历年真题精讲', desc: '深度解析历年真题，掌握解题技巧' },
      { title: '模拟考试演练', desc: '全真模拟考试环境，提升应试能力' }
    ],
    actionHint: '立即开始 AMC 备考之旅，向数学竞赛迈出第一步！',
    targetPath: '/subject-select',
    targetQuery: { level: 'amc8' }
  },
  amc10: {
    title: '美国数学测评 AMC',
    subtitle: '全球最具影响力的中学数学竞赛，培养数学思维与解题能力',
    badge: '推荐',
    heroImage: 'https://images.unsplash.com/photo-1635070041078-e363dbe005cb?auto=format&fit=crop&w=1600&q=80',
    introduction: [
      'AMC（American Mathematics Competitions）是美国数学协会（MAA）主办的全球性数学竞赛，涵盖AMC 8、AMC 10、AMC 12三个级别，分别面向8年级及以下、10年级及以下、12年级及以下的学生。',
      '该测评旨在激发学生对数学的兴趣，培养解决问题的能力和数学思维。题目设计注重考查学生的逻辑推理能力、创新思维和数学应用能力。',
      'AMC是通往更高级别数学竞赛（AIME、USAMO、IMO）的重要通道，也是申请美国顶尖高中和大学的重要学术背景之一，被全球6000多所学校认可。'
    ],
    features: [
      { title: '国际认可', desc: '全球超过6000所学校参与，成绩被国际名校认可', icon: 'el-icon-trophy', color: '#f5a623' },
      { title: '思维训练', desc: '培养逻辑思维、创新能力和问题解决技巧', icon: 'el-icon-cpu', color: '#1890ff' },
      { title: '竞赛衔接', desc: '为 AIME、USAMO、IMO 等高级竞赛打下基础', icon: 'el-icon-connection', color: '#52c41a' },
      { title: '升学助力', desc: '丰富学术背景，助力名校申请', icon: 'el-icon-school', color: '#722ed1' }
    ],
    audience: [
      '对数学有兴趣的中学生',
      '希望提升数学思维能力的学生',
      '计划参加更高级别数学竞赛的学生',
      '有志于申请国际名校的学生'
    ],
    learningContent: [
      { title: '基础知识巩固', desc: '系统复习数学核心知识点，夯实基础' },
      { title: '题型专项训练', desc: '针对 AMC 常见题型进行专项突破' },
      { title: '历年真题精讲', desc: '深度解析历年真题，掌握解题技巧' },
      { title: '模拟考试演练', desc: '全真模拟考试环境，提升应试能力' }
    ],
    actionHint: '立即开始 AMC 备考之旅，向数学竞赛迈出第一步！',
    targetPath: '/subject-select',
    targetQuery: { level: 'amc10' }
  },
  amc12: {
    title: '美国数学测评 AMC',
    subtitle: '全球最具影响力的中学数学竞赛，培养数学思维与解题能力',
    badge: '顶级',
    heroImage: 'https://images.unsplash.com/photo-1636466497217-26a8cbeaf0aa?auto=format&fit=crop&w=1600&q=80',
    introduction: [
      'AMC（American Mathematics Competitions）是美国数学协会（MAA）主办的全球性数学竞赛，涵盖AMC 8、AMC 10、AMC 12三个级别，分别面向8年级及以下、10年级及以下、12年级及以下的学生。',
      '该测评旨在激发学生对数学的兴趣，培养解决问题的能力和数学思维。题目设计注重考查学生的逻辑推理能力、创新思维和数学应用能力。',
      'AMC是通往更高级别数学竞赛（AIME、USAMO、IMO）的重要通道，也是申请美国顶尖高中和大学的重要学术背景之一，被全球6000多所学校认可。'
    ],
    features: [
      { title: '国际认可', desc: '全球超过6000所学校参与，成绩被国际名校认可', icon: 'el-icon-trophy', color: '#f5a623' },
      { title: '思维训练', desc: '培养逻辑思维、创新能力和问题解决技巧', icon: 'el-icon-cpu', color: '#1890ff' },
      { title: '竞赛衔接', desc: '为 AIME、USAMO、IMO 等高级竞赛打下基础', icon: 'el-icon-connection', color: '#52c41a' },
      { title: '升学助力', desc: '丰富学术背景，助力名校申请', icon: 'el-icon-school', color: '#722ed1' }
    ],
    audience: [
      '对数学有兴趣的中学生',
      '希望提升数学思维能力的学生',
      '计划参加更高级别数学竞赛的学生',
      '有志于申请国际名校的学生'
    ],
    learningContent: [
      { title: '基础知识巩固', desc: '系统复习数学核心知识点，夯实基础' },
      { title: '题型专项训练', desc: '针对 AMC 常见题型进行专项突破' },
      { title: '历年真题精讲', desc: '深度解析历年真题，掌握解题技巧' },
      { title: '模拟考试演练', desc: '全真模拟考试环境，提升应试能力' }
    ],
    actionHint: '立即开始 AMC 备考之旅，向数学竞赛迈出第一步！',
    targetPath: '/subject-select',
    targetQuery: { level: 'amc12' }
  },
  kangaroo: {
    title: '袋鼠数学思维挑战',
    subtitle: '全球最大规模的青少年数学思维挑战活动，培养数学兴趣与思维能力',
    heroImage: 'https://images.unsplash.com/photo-1509228468518-180dd4864904?auto=format&fit=crop&w=1600&q=80',
    introduction: [
      '袋鼠数学思维挑战（Math Kangaroo）起源于澳大利亚，是全球规模最大的青少年数学竞赛活动，每年有超过600万学生参与。',
      '该活动旨在用有趣、生动的方式激发学生对数学的兴趣，培养数学思维能力和解决问题的技巧，让学生在快乐中学习数学。',
      '袋鼠数学分为6个级别，覆盖小学到高中各年龄段，题目设计注重趣味性和思维性，是培养数学兴趣的绝佳选择。'
    ],
    features: [
      { title: '全球最大', desc: '每年超过600万学生参与的数学盛会', icon: 'el-icon-s-data', color: '#52c41a' },
      { title: '趣味学习', desc: '题目生动有趣，激发数学兴趣', icon: 'el-icon-magic-stick', color: '#faad14' },
      { title: '分级挑战', desc: '6个级别覆盖各年龄段学生', icon: 'el-icon-s-unfold', color: '#1890ff' },
      { title: '思维培养', desc: '注重培养逻辑思维和创新能力', icon: 'el-icon-cpu', color: '#722ed1' }
    ],
    audience: [
      '小学1年级至高中3年级的学生',
      '对数学有兴趣，希望提升思维能力的学生',
      '希望通过趣味方式学习数学的学生',
      '为参加更高级别数学竞赛做准备的学生'
    ],
    learningContent: [
      { title: '趣味数学入门', desc: '通过有趣的题目激发数学学习兴趣' },
      { title: '思维方法训练', desc: '学习各种数学思维方法和解题技巧' },
      { title: '分级专项练习', desc: '针对不同级别进行专项训练' },
      { title: '真题实战演练', desc: '历年真题练习，熟悉考试形式' }
    ],
    actionHint: '选择您的级别，开启趣味数学之旅！',
    targetPath: '/subject-select'
  },
  gesp: {
    title: 'CCF-GESP 编程能力等级认证',
    subtitle: '中国计算机学会编程能力等级认证，培养计算思维与编程能力',
    heroImage: 'https://images.unsplash.com/photo-1517694712202-14dd9538aa97?auto=format&fit=crop&w=1600&q=80',
    introduction: [
      'GESP（Grade Examination of Software Programming）是由中国计算机学会（CCF）主办的青少年编程能力等级认证考试。',
      '该认证分为8个等级，涵盖 C++、Python 等主流编程语言，旨在科学评价青少年的编程能力，培养计算思维和软件编程能力。',
      'GESP 认证是信息学竞赛的重要基础，也是展示编程能力的权威证明，对于升学和未来发展都有重要价值。'
    ],
    features: [
      { title: '权威认证', desc: 'CCF 官方认证，含金量高', icon: 'el-icon-document-checked', color: '#1890ff' },
      { title: '分级体系', desc: '8个等级，循序渐进提升能力', icon: 'el-icon-s-unfold', color: '#52c41a' },
      { title: '竞赛基础', desc: '为 CSP-J/S、NOI 等竞赛打基础', icon: 'el-icon-connection', color: '#722ed1' },
      { title: '能力证明', desc: '编程能力的权威证明', icon: 'el-icon-postcard', color: '#faad14' }
    ],
    audience: [
      '对编程有兴趣的中小学生',
      '希望系统学习编程的学生',
      '计划参加信息学竞赛的学生',
      '希望获得编程能力认证的学生'
    ],
    learningContent: [
      { title: '编程基础入门', desc: '学习编程语言基础语法和概念' },
      { title: '算法思维培养', desc: '培养算法思维和问题分析能力' },
      { title: '等级专项训练', desc: '针对各等级考试内容进行训练' },
      { title: '真题模拟练习', desc: '历年真题练习，熟悉考试形式' }
    ],
    actionHint: '开始编程学习之旅，获取 CCF 官方认证！',
    targetPath: '/subject-select'
  }
}

// 袋鼠数学级别配置
const kangarooLevels = [
  { value: 1, name: 'Level 1', grade: '小学1-2年级' },
  { value: 2, name: 'Level 2', grade: '小学3-4年级' },
  { value: 3, name: 'Level 3', grade: '小学5-6年级' },
  { value: 4, name: 'Level 4', grade: '初中7-8年级' },
  { value: 5, name: 'Level 5', grade: '初中9年级-高一' },
  { value: 6, name: 'Level 6', grade: '高中10-12年级' }
]

export default {
  name: 'ProjectIntro',
  data() {
    return {
      selectedLevel: null,
      kangarooLevels,
      checkingPlan: false,
      amcLongRead,
      articleChapters,
      kangarooLongRead,
      kangarooChapters,
      gespLongRead,
      gespChapters
    }
  },
  computed: {
    projectType() {
      return this.$route.params.type || 'amc8'
    },
    projectData() {
      return projectConfigs[this.projectType] || projectConfigs.amc8
    },
    isLoggedIn() {
      const userInfo = localStorage.getItem('userInfo')
      return !!(userInfo && getToken())
    },
    isAmcProject() {
      return ['amc8', 'amc10', 'amc12'].includes(this.projectType)
    }
  },
  methods: {
    goLogin() {
      this.$router.push({
        path: '/login',
        query: { redirect: this.$route.fullPath }
      })
    },
    goDashboard() {
      this.$router.push('/dashboard')
    },
    scrollToChapter(index) {
      const chapter = this.$refs[`chapter${index}`]
      if (chapter) {
        chapter.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    },
    getKnowledgeBackgroundKey(backgroundNumber) {
      const backgroundMap = {
        1: 'never',
        2: 'basic',
        3: 'proficient'
      }
      return backgroundMap[backgroundNumber] || 'never'
    },
    // 获取用户对应AMC级别已选计划（amcMark==1）的详情
    async getAmcSelectedPlan(level) {
      try {
        const userInfo = getTokenInfo()
        if (!userInfo || !userInfo.id) {
          return null
        }

        const levelToRepo = { 'amc8': 14, 'amc10': 15, 'amc12': 16 }
        const targetRepo = levelToRepo[level]
        if (!targetRepo) return null

        const response = await getAmcInfoByUserId(userInfo.id)
        if (response.code === 1 && response.data && response.data.length > 0) {
          // 查找对应级别且 amcMark=1 的计划
          const matchedPlan = response.data.find(
            item => Number(item.amcRepo) === targetRepo && Number(item.amcMark) === 1
          )
          if (!matchedPlan) return null
          return {
            level,
            year: matchedPlan.year,
            knowledgeBackground: this.getKnowledgeBackgroundKey(Number(matchedPlan.level))
          }
        }
        return null
      } catch (error) {
        console.error('检查AMC学习计划失败:', error)
        return null
      }
    },
    async enterStudy() {
      if (!this.isLoggedIn) {
        this.$message.warning('请先登录后再进入学习')
        this.goLogin()
        return
      }

      const data = this.projectData

      // GESP 暂未开放
      if (data.comingSoon) {
        this.$message.info('该课程即将上线，敬请期待！')
        return
      }

      // 袋鼠数学需要选择级别
      if (this.projectType === 'kangaroo') {
        if (!this.selectedLevel) {
          this.$message.warning('请先选择您的级别')
          return
        }
        this.$router.push({
          path: data.targetPath,
          query: { level: String(this.selectedLevel) }
        })
        return
      }

      // AMC 项目和其他项目，直接跳转到课程主页
      this.$router.push({
        path: data.targetPath,
        query: data.targetQuery || {}
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.project-intro {
  min-height: 100vh;
  background: #f5f7fa;
}

// Header
.intro-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.header-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.back-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #666;
  text-decoration: none;
  font-size: 14px;
  transition: color 0.3s;

  &:hover {
    color: #1890ff;
  }
}

.header-title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

// Main Content
.intro-main {
  padding-top: 60px;
}

// Hero Section
.hero-section {
  position: relative;
  height: 400px;
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
}

.hero-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.85) 0%, rgba(118, 75, 162, 0.85) 100%);
}

.hero-content {
  position: relative;
  z-index: 1;
  text-align: center;
  color: #fff;
  padding: 0 24px;
}

.hero-badge {
  display: inline-block;
  padding: 6px 16px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 20px;
  font-size: 14px;
  margin-bottom: 16px;
}

.hero-title {
  font-size: 42px;
  font-weight: 700;
  margin: 0 0 16px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.hero-subtitle {
  font-size: 18px;
  opacity: 0.9;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
}

// Sections
.section {
  padding: 60px 0;
}

.section-container {
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 24px;
}

.section-title {
  font-size: 28px;
  font-weight: 600;
  color: #333;
  margin: 0 0 32px;
  display: flex;
  align-items: center;
  gap: 12px;

  i {
    color: #1890ff;
  }
}

// Intro Section
.intro-section {
  background: #fff;
}

.intro-content {
  p {
    font-size: 16px;
    line-height: 1.8;
    color: #666;
    margin: 0 0 16px;

    &:last-child {
      margin-bottom: 0;
    }
  }
}

// Features Section
.features-section {
  background: #f5f7fa;
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
}

.feature-card {
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  transition: transform 0.3s, box-shadow 0.3s;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
  }
}

.feature-icon {
  width: 56px;
  height: 56px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;

  i {
    font-size: 24px;
    color: #fff;
  }
}

.feature-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin: 0 0 8px;
}

.feature-desc {
  font-size: 14px;
  color: #999;
  margin: 0;
  line-height: 1.5;
}

// Audience Section
.audience-section {
  background: #fff;
}

.audience-list {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
}

.audience-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  background: #f5f7fa;
  border-radius: 8px;

  i {
    color: #52c41a;
    font-size: 18px;
  }

  span {
    font-size: 15px;
    color: #333;
  }
}

// Content Section
.content-section {
  background: #f5f7fa;
}

.content-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.content-item {
  display: flex;
  align-items: flex-start;
  gap: 20px;
  padding: 24px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.content-number {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 600;
  flex-shrink: 0;
}

.content-info {
  h4 {
    font-size: 16px;
    font-weight: 600;
    color: #333;
    margin: 0 0 8px;
  }

  p {
    font-size: 14px;
    color: #666;
    margin: 0;
    line-height: 1.5;
  }
}

// Level Section (Kangaroo)
.level-section {
  background: #fff;
}

.level-hint {
  font-size: 15px;
  color: #666;
  margin: 0 0 24px;
}

.level-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;

  @media (max-width: 700px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 450px) {
    grid-template-columns: 1fr;
  }
}

.level-card {
  padding: 24px;
  background: #f5f7fa;
  border-radius: 12px;
  text-align: center;
  cursor: pointer;
  border: 2px solid transparent;
  transition: all 0.3s;

  &:hover {
    border-color: #1890ff;
    background: #f0f7ff;
  }

  &.active {
    border-color: #1890ff;
    background: #e6f7ff;

    .level-badge {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: #fff;
    }
  }
}

.level-badge {
  display: inline-block;
  padding: 6px 16px;
  background: #e6e6e6;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  color: #666;
  margin-bottom: 12px;
  transition: all 0.3s;
}

.level-name {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
}

.level-grade {
  font-size: 14px;
  color: #999;
}

// Action Section
.action-section {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 80px 0;
}

.action-box {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 40px;

  @media (max-width: 600px) {
    flex-direction: column;
    text-align: center;
  }
}

.action-info {
  h3 {
    font-size: 28px;
    font-weight: 600;
    color: #fff;
    margin: 0 0 12px;
  }

  p {
    font-size: 16px;
    color: rgba(255, 255, 255, 0.85);
    margin: 0;
  }
}

.action-btn {
  padding: 16px 48px;
  font-size: 18px;
  font-weight: 600;
  border-radius: 30px;
  background: #fff;
  color: #667eea;
  border: none;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  transition: all 0.3s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.2);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  i {
    margin-right: 8px;
  }
}

// Footer
.intro-footer {
  background: #1a1a2e;
  padding: 24px 0;
  text-align: center;

  p {
    color: rgba(255, 255, 255, 0.6);
    font-size: 14px;
    margin: 0;
  }
}

// ========== AMC 深度专题样式 ==========
.article-section {
  background: #fff;
  padding: 0;
}

// 文章头部横幅
.article-hero {
  position: relative;
  min-height: 480px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px 24px;
}

.article-hero-content {
  text-align: center;
  color: #fff;
  max-width: 800px;
}

.article-tag {
  display: inline-block;
  padding: 8px 20px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 30px;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 24px;
}

.article-title {
  font-size: 48px;
  font-weight: 700;
  margin: 0 0 16px;
  line-height: 1.2;
  text-shadow: 0 2px 20px rgba(0, 0, 0, 0.15);
}

.article-subtitle {
  font-size: 22px;
  font-weight: 400;
  opacity: 0.95;
  margin: 0 0 32px;
}

.article-meta {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 24px;
  font-size: 14px;
  opacity: 0.8;
}

// 章节导航
.article-toc {
  background: #f8f9fa;
  padding: 40px 24px;
}

.toc-container {
  max-width: 1000px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 16px;
}

.toc-card {
  background: #fff;
  border-radius: 12px;
  padding: 24px 16px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  border: 2px solid transparent;
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 24px rgba(102, 126, 234, 0.15);
    border-color: #667eea;
  }
}

.toc-number {
  font-size: 28px;
  font-weight: 700;
  color: #667eea;
  margin-bottom: 8px;
}

.toc-title {
  font-size: 14px;
  font-weight: 500;
  color: #333;
}

// 导语
.article-lead {
  background: #fff;
  padding: 60px 24px;
  border-bottom: 1px solid #e8e8e8;
}

.lead-container {
  max-width: 800px;
  margin: 0 auto;
  position: relative;
}

.lead-quote-mark {
  font-size: 120px;
  font-family: Georgia, serif;
  color: #667eea;
  opacity: 0.2;
  position: absolute;
  top: -40px;
  left: -20px;
  line-height: 1;
}

.lead-text {
  font-size: 18px;
  line-height: 1.9;
  color: #333;
  margin: 0 0 24px;
  position: relative;
  z-index: 1;
}

.lead-highlight {
  font-size: 18px;
  line-height: 1.9;
  color: #667eea;
  font-weight: 500;
  margin: 0;
  padding: 20px 24px;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%);
  border-radius: 12px;
  border-left: 4px solid #667eea;
}

// 章节
.article-chapter {
  padding: 80px 24px;
  max-width: 1100px;
  margin: 0 auto;
  &:nth-child(even) {
    background: #fafbfc;
  }
}

.chapter-header {
  text-align: left;
  margin-bottom: 40px;
  &.center {
    text-align: center;
  }
}

.chapter-badge {
  display: inline-block;
  padding: 6px 16px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 500;
  margin-bottom: 16px;
}

.chapter-title {
  font-size: 36px;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0 0 12px;
}

.chapter-subtitle {
  font-size: 18px;
  color: #666;
  margin: 0;
}

.chapter-content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 48px;
  align-items: start;
  &.content-reverse {
    direction: rtl;
    > * {
      direction: ltr;
    }
  }
}

.content-text {
  p {
    font-size: 16px;
    line-height: 1.9;
    color: #333;
    margin: 0 0 20px;
    strong {
      color: #667eea;
      font-weight: 600;
    }
  }
}

.info-highlight {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 20px 24px;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.08) 0%, rgba(118, 75, 162, 0.08) 100%);
  border-radius: 12px;
  margin: 24px 0;
  i {
    font-size: 24px;
    color: #667eea;
    flex-shrink: 0;
    margin-top: 2px;
  }
  span {
    font-size: 15px;
    line-height: 1.7;
    color: #444;
  }
}

.highlight-number {
  color: #667eea;
  font-size: 1.2em;
  font-weight: 700;
}

// 图片
.chapter-figure {
  margin: 0;
  img {
    width: 100%;
    height: auto;
    border-radius: 16px;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
    transition: transform 0.3s, box-shadow 0.3s;
    &:hover {
      transform: translateY(-4px);
      box-shadow: 0 30px 60px rgba(0, 0, 0, 0.15);
    }
  }
  figcaption {
    margin-top: 16px;
    text-align: center;
    font-size: 14px;
    color: #888;
  }
  &.wide {
    grid-column: 1 / -1;
    max-width: 900px;
    margin: 40px auto 0;
  }
}

// 章节内小标题
.section-title-alt {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 32px 0 16px;
  display: flex;
  align-items: center;
  gap: 10px;
  i {
    color: #667eea;
    font-size: 20px;
  }
}

// 数据流程
.data-flow {
  margin: 24px 0;
  padding: 24px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
}

.flow-item {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
  &:last-child {
    margin-bottom: 0;
  }
}

.flow-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #667eea;
  flex-shrink: 0;
  &.gold {
    background: linear-gradient(135deg, #f5a623 0%, #f7b733 100%);
    box-shadow: 0 0 0 4px rgba(245, 166, 35, 0.2);
  }
}

.flow-text {
  strong {
    display: block;
    font-size: 16px;
    color: #333;
    margin-bottom: 4px;
  }
  span {
    font-size: 14px;
    color: #888;
  }
}

// 统计卡片
.stat-card-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin: 24px 0;
}

.stat-card {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 16px;
  padding: 24px;
  text-align: center;
  color: #fff;
}

.stat-number-large {
  font-size: 36px;
  font-weight: 700;
  margin-bottom: 8px;
}

.stat-label {
  font-size: 14px;
  opacity: 0.9;
}

// 引用框
.quote-box {
  margin: 32px 0;
  padding: 24px 28px;
  background: #f8f9fa;
  border-radius: 12px;
  border-left: 4px solid #667eea;
  p {
    font-size: 16px;
    line-height: 1.8;
    color: #555;
    font-style: italic;
    margin: 0;
  }
}

// 数据可视化章节
.chapter-data {
  background: linear-gradient(180deg, #f8f9fa 0%, #fff 100%);
}

.data-showcase {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 48px;
  margin-bottom: 40px;
}

.data-growth {
  background: #fff;
  border-radius: 20px;
  padding: 32px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
}

.growth-bar {
  display: flex;
  align-items: flex-end;
  justify-content: center;
  gap: 60px;
  margin-bottom: 24px;
  height: 200px;
}

.bar-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.bar-year {
  font-size: 14px;
  color: #888;
  font-weight: 500;
}

.bar-line {
  width: 60px;
  height: calc(var(--height) * 2);
  background: #e0e0e0;
  border-radius: 8px 8px 0 0;
  transition: all 0.3s;
  &.highlight {
    background: linear-gradient(180deg, #667eea 0%, #764ba2 100%);
  }
}

.bar-value {
  font-size: 16px;
  font-weight: 600;
  color: #666;
  &.highlight {
    color: #667eea;
    font-size: 20px;
  }
}

.growth-caption {
  text-align: center;
  font-size: 14px;
  color: #888;
  margin: 0;
}

.data-features {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.feature-item {
  background: #fff;
  border-radius: 16px;
  padding: 24px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
  i {
    font-size: 28px;
    color: #667eea;
  }
  h4 {
    font-size: 16px;
    font-weight: 600;
    color: #333;
    margin: 0 0 4px;
  }
  p {
    font-size: 14px;
    color: #888;
    margin: 0;
  }
}

// 指南章节
.chapter-guide {
  background: #fff;
}

.guide-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  margin-bottom: 48px;
}

.guide-card {
  background: #f8f9fa;
  border-radius: 20px;
  padding: 32px 24px;
  text-align: center;
  transition: all 0.3s;
  border: 2px solid transparent;
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 30px rgba(0, 0, 0, 0.08);
    border-color: #667eea;
  }
  &.highlight {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: #fff;
    h3, .guide-date, .guide-highlight {
      color: #fff;
    }
    .guide-note, .guide-or {
      color: rgba(255, 255, 255, 0.8);
    }
    .guide-icon {
      background: rgba(255, 255, 255, 0.2);
      i {
        color: #fff;
      }
    }
  }
}

.guide-icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: rgba(102, 126, 234, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  i {
    font-size: 28px;
    color: #667eea;
  }
}

.guide-card h3 {
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin: 0 0 16px;
}

.guide-date {
  font-size: 15px;
  color: #667eea;
  font-weight: 500;
  margin: 0 0 8px;
}

.guide-highlight {
  font-size: 15px;
  color: #333;
  font-weight: 500;
  margin: 0;
}

.guide-or {
  font-size: 13px;
  color: #888;
  margin: 8px 0;
}

.guide-note {
  font-size: 13px;
  color: #888;
  margin: 16px 0 0;
}

// 结语
.article-epilogue {
  background: linear-gradient(135deg, #1a1a2e 0%, #2d2d44 100%);
  padding: 80px 24px;
  color: #fff;
}

.epilogue-container {
  max-width: 800px;
  margin: 0 auto;
}

.epilogue-header {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 24px;
  i {
    font-size: 18px;
  }
}

.epilogue-title {
  font-size: 36px;
  font-weight: 700;
  margin: 0 0 32px;
  background: linear-gradient(135deg, #667eea 0%, #a78bfa 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.epilogue-text {
  font-size: 18px;
  line-height: 1.9;
  color: rgba(255, 255, 255, 0.9);
  margin: 0 0 32px;
}

.epilogue-highlight {
  padding: 24px 28px;
  background: rgba(102, 126, 234, 0.15);
  border-radius: 16px;
  border-left: 4px solid #667eea;
  margin: 32px 0;
  p {
    font-size: 17px;
    line-height: 1.8;
    color: #fff;
    margin: 0;
  }
}

.epilogue-closing {
  font-size: 18px;
  line-height: 1.9;
  color: rgba(255, 255, 255, 0.85);
  margin: 0;
  strong {
    color: #a78bfa;
  }
}

// 响应式适配
@media (max-width: 900px) {
  .article-title {
    font-size: 32px;
  }
  .toc-container {
    grid-template-columns: repeat(3, 1fr);
  }
  .chapter-content {
    grid-template-columns: 1fr;
    gap: 32px;
  }
  .data-showcase {
    grid-template-columns: 1fr;
  }
  .guide-grid {
    grid-template-columns: 1fr;
  }
  .stat-card-row {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 600px) {
  .article-hero {
    min-height: 360px;
    padding: 40px 20px;
  }
  .article-title {
    font-size: 26px;
  }
  .article-subtitle {
    font-size: 16px;
  }
  .toc-container {
    grid-template-columns: repeat(2, 1fr);
  }
  .chapter-title {
    font-size: 26px;
  }
  .epilogue-title {
    font-size: 28px;
  }
}

// ========== 袋鼠数学专用样式 ==========
// 对比表格
.comparison-table {
  margin: 24px 0;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
}

.table-header {
  display: grid;
  grid-template-columns: 1fr 1.2fr 1.2fr;
  background: linear-gradient(135deg, #52c41a 0%, #1890ff 100%);
  color: #fff;
  padding: 16px;
  font-weight: 600;
  font-size: 15px;
}

.table-row {
  display: grid;
  grid-template-columns: 1fr 1.2fr 1.2fr;
  background: #fff;
  padding: 14px 16px;
  border-bottom: 1px solid #f0f0f0;
  font-size: 14px;
  align-items: center;
  &:last-child {
    border-bottom: none;
  }
  &:nth-child(even) {
    background: #fafafa;
  }
}

.highlight-cell {
  color: #52c41a;
  font-weight: 600;
}

// 袋鼠曲线路径
.kangaroo-path {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin: 28px 0;
  padding: 24px;
  background: #f8f9fa;
  border-radius: 16px;
  flex-wrap: wrap;
}

.path-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 8px;
  .step-num {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    background: #e0e0e0;
    color: #666;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 18px;
  }
  .step-text {
    font-size: 13px;
    color: #666;
    max-width: 120px;
    line-height: 1.4;
  }
  &.highlight {
    .step-num {
      background: linear-gradient(135deg, #52c41a 0%, #1890ff 100%);
      color: #fff;
    }
    .step-text {
      color: #333;
      font-weight: 500;
    }
  }
}

.path-arrow {
  font-size: 24px;
  color: #ccc;
  font-weight: 300;
}

// 统计网格
.stat-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin: 24px 0;
}

.stat-box {
  background: linear-gradient(135deg, rgba(82, 196, 26, 0.08) 0%, rgba(24, 144, 255, 0.08) 100%);
  border-radius: 12px;
  padding: 20px 16px;
  text-align: center;
  border: 1px solid rgba(82, 196, 26, 0.15);
  .stat-value {
    font-size: 28px;
    font-weight: 700;
    color: #52c41a;
    margin-bottom: 6px;
  }
  .stat-desc {
    font-size: 12px;
    color: #666;
    line-height: 1.4;
  }
}

// 简历对比
.resume-compare {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  margin: 28px 0;
  padding: 24px;
  background: #f8f9fa;
  border-radius: 16px;
}

.resume-card {
  background: #fff;
  border-radius: 12px;
  padding: 20px 24px;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  flex: 1;
  .resume-label {
    font-size: 13px;
    color: #888;
    margin-bottom: 8px;
  }
  p {
    font-size: 15px;
    color: #666;
    margin: 0;
    line-height: 1.5;
  }
  &.highlight {
    border: 2px solid #52c41a;
    box-shadow: 0 4px 16px rgba(82, 196, 26, 0.15);
    p {
      color: #333;
      strong {
        color: #52c41a;
      }
    }
  }
}

.resume-vs {
  font-size: 18px;
  font-weight: 600;
  color: #ccc;
}

// 分数线表格
.score-table {
  margin: 24px 0;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
}

.score-header {
  display: grid;
  grid-template-columns: 2fr 1fr 1.2fr 1fr;
  background: linear-gradient(135deg, #52c41a 0%, #1890ff 100%);
  color: #fff;
  padding: 14px 16px;
  font-weight: 600;
  font-size: 14px;
}

.score-row {
  display: grid;
  grid-template-columns: 2fr 1fr 1.2fr 1fr;
  background: #fff;
  padding: 12px 16px;
  border-bottom: 1px solid #f0f0f0;
  font-size: 14px;
  align-items: center;
  &:last-child {
    border-bottom: none;
  }
}

.highlight-score {
  color: #52c41a;
  font-weight: 700;
}

// 规则框
.rule-box {
  background: #f8f9fa;
  border-radius: 16px;
  padding: 24px;
  margin: 24px 0;
  h4 {
    font-size: 16px;
    font-weight: 600;
    color: #333;
    margin: 0 0 16px;
    i {
      color: #52c41a;
      margin-right: 8px;
    }
  }
}

.rule-list {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}

.rule-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: #fff;
  border-radius: 8px;
  font-size: 14px;
  i {
    font-size: 16px;
  }
  &.correct i {
    color: #52c41a;
  }
  &.wrong i {
    color: #f5222d;
  }
  &.skip i {
    color: #999;
  }
}

// 策略列表
.strategy-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin: 24px 0;
}

.strategy-item {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 16px 20px;
  background: #f8f9fa;
  border-radius: 12px;
  .strategy-num {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: linear-gradient(135deg, #52c41a 0%, #1890ff 100%);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 14px;
    flex-shrink: 0;
  }
  .strategy-text {
    strong {
      display: block;
      font-size: 15px;
      color: #333;
      margin-bottom: 4px;
    }
    span {
      font-size: 13px;
      color: #888;
    }
  }
}

// 奖项区域
.award-section {
  margin-top: 40px;
  h3 {
    font-size: 18px;
    font-weight: 600;
    color: #333;
    margin: 0 0 20px;
    i {
      color: #f5a623;
      margin-right: 8px;
    }
  }
}

.award-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 12px;
}

.award-item {
  text-align: center;
  padding: 20px 12px;
  border-radius: 12px;
  .award-name {
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 6px;
  }
  .award-rate {
    font-size: 12px;
    opacity: 0.9;
  }
  &.super-gold {
    background: linear-gradient(135deg, #ffd700 0%, #ffaa00 100%);
    color: #fff;
  }
  &.gold {
    background: linear-gradient(135deg, #ffec3d 0%, #f5a623 100%);
    color: #fff;
  }
  &.silver {
    background: linear-gradient(135deg, #e8e8e8 0%, #a8a8a8 100%);
    color: #fff;
  }
  &.bronze {
    background: linear-gradient(135deg, #d4a574 0%, #b87333 100%);
    color: #fff;
  }
  &.skill {
    background: linear-gradient(135deg, #69c0ff 0%, #1890ff 100%);
    color: #fff;
  }
}

// ========== GESP专用样式 ==========
.gesp-features {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin: 24px 0;
}

.gesp-feature {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 16px 20px;
  background: #f8f9fa;
  border-radius: 12px;
  i {
    font-size: 28px;
    flex-shrink: 0;
  }
  h4 {
    font-size: 15px;
    font-weight: 600;
    color: #333;
    margin: 0 0 4px;
  }
  p {
    font-size: 13px;
    color: #888;
    margin: 0;
  }
}

// 等级时间线
.level-timeline {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin: 24px 0;
}

.level-group {
  background: #f8f9fa;
  border-radius: 16px;
  padding: 20px 24px;
  h4 {
    font-size: 16px;
    font-weight: 600;
    color: #333;
    margin: 0 0 10px;
    i {
      margin-right: 8px;
    }
  }
  p {
    font-size: 14px;
    color: #666;
    margin: 0 0 12px;
    line-height: 1.6;
  }
}

.level-tags {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.level-tag {
  padding: 6px 12px;
  background: rgba(114, 46, 209, 0.1);
  color: #722ed1;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  &.blue {
    background: rgba(24, 144, 255, 0.1);
    color: #1890ff;
  }
}

// 路径流程
.path-flow {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin: 28px 0;
  padding: 24px;
  background: #f8f9fa;
  border-radius: 16px;
  flex-wrap: wrap;
}

.flow-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 8px;
  .step-icon {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 16px;
  }
  .step-info {
    h4 {
      font-size: 13px;
      font-weight: 600;
      color: #333;
      margin: 0 0 2px;
    }
    p {
      font-size: 11px;
      color: #888;
      margin: 0;
    }
  }
}

.flow-arrow {
  font-size: 20px;
  color: #ccc;
}

.benefit-list {
  list-style: none;
  padding: 0;
  margin: 20px 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  li {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 14px;
    color: #333;
    i {
      font-size: 16px;
    }
  }
}

// 备考区域
.prep-section {
  margin-top: 40px;
  h3 {
    font-size: 18px;
    font-weight: 600;
    color: #333;
    margin: 0 0 20px;
    i {
      margin-right: 8px;
    }
  }
}

.prep-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.prep-card {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 20px;
  h4 {
    font-size: 15px;
    font-weight: 600;
    color: #333;
    margin: 0 0 10px;
    i {
      color: #722ed1;
      margin-right: 8px;
    }
  }
  p {
    font-size: 13px;
    color: #666;
    margin: 0;
    line-height: 1.6;
  }
}

// 修复CSS兼容性 - 添加标准background-clip
.epilogue-title {
  background: linear-gradient(135deg, #667eea 0%, #a78bfa 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

// ========== GESP专用样式（新版5章结构） ==========
// GESP路径流程
.gesp-path-flow {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 8px;
  margin: 24px 0;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 12px;
}

.gesp-step {
  padding: 10px 16px;
  background: #fff;
  border-radius: 8px;
  font-size: 13px;
  color: #666;
  box-shadow: 0 2px 4px rgba(0,0,0,0.04);
  &.highlight {
    background: linear-gradient(135deg, #722ed1 0%, #1890ff 100%);
    color: #fff;
    font-weight: 600;
  }
}

.gesp-arrow {
  color: #ccc;
  font-size: 16px;
}

// 跳级规则卡片
.jump-rules {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin: 24px 0;
}

.rule-card {
  background: #f8f9fa;
  border-radius: 16px;
  padding: 24px;
  text-align: center;
  .rule-icon {
    width: 56px;
    height: 56px;
    border-radius: 50%;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    font-weight: 700;
    margin: 0 auto 16px;
  }
  .rule-text {
    h4 {
      font-size: 15px;
      font-weight: 600;
      color: #333;
      margin: 0 0 8px;
    }
    p {
      font-size: 13px;
      color: #666;
      margin: 0;
      line-height: 1.6;
    }
  }
}

// 考试信息卡片
.exam-info-cards {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin: 24px 0;
}

.exam-card {
  background: linear-gradient(135deg, rgba(114, 46, 209, 0.08) 0%, rgba(24, 144, 255, 0.08) 100%);
  border-radius: 16px;
  padding: 24px;
  text-align: center;
  border: 1px solid rgba(114, 46, 209, 0.1);
  .exam-label {
    font-size: 13px;
    color: #888;
    margin-bottom: 8px;
  }
  .exam-value {
    font-size: 18px;
    font-weight: 700;
    color: #722ed1;
    margin-bottom: 8px;
  }
  .exam-detail {
    font-size: 12px;
    color: #666;
    line-height: 1.6;
  }
}

// 费用对比
.cost-comparison {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 32px;
  margin: 32px 0;
  padding: 32px;
  background: #f8f9fa;
  border-radius: 20px;
  flex-wrap: wrap;
}

.cost-item {
  text-align: center;
  &.gesp {
    padding: 32px 40px;
    background: linear-gradient(135deg, #722ed1 0%, #1890ff 100%);
    border-radius: 20px;
    color: #fff;
    .cost-name {
      font-size: 18px;
      font-weight: 600;
      margin-bottom: 12px;
    }
    .cost-total {
      font-size: 42px;
      font-weight: 700;
      margin-bottom: 12px;
    }
    .cost-desc {
      font-size: 13px;
      opacity: 0.9;
      line-height: 1.6;
    }
  }
  &.other {
    padding: 20px 24px;
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
    .cost-name {
      font-size: 14px;
      color: #666;
      margin-bottom: 8px;
    }
    .cost-total {
      font-size: 20px;
      font-weight: 700;
      color: #f5a623;
    }
  }
}

.cost-vs {
  font-size: 18px;
  font-weight: 600;
  color: #ccc;
}

.cost-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

// 报考手册
.guide-manual {
  margin-top: 48px;
  padding: 40px;
  background: #f8f9fa;
  border-radius: 24px;
  h3 {
    font-size: 20px;
    font-weight: 600;
    color: #333;
    margin: 0 0 32px;
    text-align: center;
    i {
      margin-right: 8px;
    }
  }
}

.manual-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  margin-bottom: 32px;
}

.manual-card {
  background: #fff;
  border-radius: 16px;
  padding: 24px;
  h4 {
    font-size: 16px;
    font-weight: 600;
    color: #333;
    margin: 0 0 16px;
    i {
      color: #722ed1;
      margin-right: 8px;
    }
  }
  p {
    font-size: 14px;
    color: #555;
    margin: 0 0 8px;
    line-height: 1.7;
    &:last-child {
      margin-bottom: 0;
    }
  }
}

.contact-info {
  text-align: center;
  padding: 24px;
  background: #fff;
  border-radius: 16px;
  h4 {
    font-size: 16px;
    font-weight: 600;
    color: #333;
    margin: 0 0 16px;
    i {
      color: #722ed1;
      margin-right: 8px;
    }
  }
  p {
    font-size: 14px;
    color: #666;
    margin: 0 0 8px;
  }
}

// 响应式适配
@media (max-width: 900px) {
  .comparison-table,
  .score-table {
    font-size: 12px;
  }
  .table-header,
  .table-row,
  .score-header,
  .score-row {
    padding: 10px 12px;
  }
  .kangaroo-path {
    gap: 12px;
  }
  .path-arrow {
    display: none;
  }
  .stat-grid {
    grid-template-columns: 1fr;
  }
  .award-grid {
    grid-template-columns: repeat(3, 1fr);
  }
  .prep-grid {
    grid-template-columns: 1fr;
  }
  .path-flow {
    gap: 8px;
  }
  .flow-arrow {
    display: none;
  }
  // GESP responsive
  .jump-rules {
    grid-template-columns: 1fr;
  }
  .exam-info-cards {
    grid-template-columns: 1fr;
  }
  .cost-comparison {
    flex-direction: column;
    gap: 20px;
  }
  .cost-vs {
    transform: rotate(90deg);
  }
  .manual-grid {
    grid-template-columns: 1fr;
  }
  .gesp-path-flow {
    .gesp-arrow {
      transform: rotate(90deg);
    }
  }
}

@media (max-width: 600px) {
  .resume-compare {
    flex-direction: column;
    gap: 12px;
  }
  .resume-vs {
    transform: rotate(90deg);
  }
  .award-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  .rule-list {
    flex-direction: column;
    gap: 8px;
  }
}

</style>
